import {announce} from '@github-ui/aria-live'
import {useState} from 'react'
import {ActionList, Flash, IconButton, Link} from '@primer/react'
import {CircleSlashIcon} from '@primer/octicons-react'
import {useEnableAutoMergeMutation} from '../../hooks/mutations/use-enable-auto-merge-mutation'
import {ButtonWithDropdown} from '../sections/common/ButtonWithDropdown'
import {MergeDropdownOption} from '../sections/merge-section/MergeDropdownOption'
import {MergeQueueMethod} from '../../types'
import {useSidebarMergeData} from './use-sidebar-merge-data'
import styles from './SidebarMergeWhenReadyButton.module.css'

export function SidebarEnqueueButton() {
  const {mergeBoxPullRequest} = useSidebarMergeData()
  const baseRefName = mergeBoxPullRequest?.baseRefName ?? 'main'
  const mergeQueue = mergeBoxPullRequest?.mergeQueue ?? null
  const viewerCanAddToMergeQueueSolo = mergeBoxPullRequest?.viewerCanAddToMergeQueueSolo ?? false

  const [confirming, setConfirming] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<MergeQueueMethod>(MergeQueueMethod.GROUP)
  const [errorMessage, setErrorMessage] = useState<string>()

  const {mutate: enableAutoMerge, isPending} = useEnableAutoMergeMutation({
    onError: (e: Error) => {
      setErrorMessage(e.message)
      announce(e.message)
    },
    onSuccess: () => {
      setConfirming(false)
      setErrorMessage(undefined)
    },
  })

  const primaryLabel = confirming
    ? selectedMethod === MergeQueueMethod.SOLO
      ? 'Confirm queue and force solo merge'
      : 'Confirm merge when ready'
    : selectedMethod === MergeQueueMethod.SOLO
      ? 'Queue and force solo merge'
      : 'Merge when ready'

  return (
    <div className="d-flex flex-column gap-2 pt-2">
      {errorMessage && <Flash variant="danger">{errorMessage}</Flash>}
      <div className={styles.buttonRow}>
        <div className={styles.primaryButton}>
          <ButtonWithDropdown
            hideSecondaryButton={confirming || !viewerCanAddToMergeQueueSolo}
            secondaryButtonAriaLabel="Select merge queue method"
            isPrimary
            loading={isPending}
            loadingAnnouncement="Merging when ready"
            actionList={
              <ActionList selectionVariant="single">
                <MergeDropdownOption
                  primaryText="Queue and merge in a group"
                  secondaryText={`This pull request will be automatically grouped with other pull requests and merged into ${baseRefName}.`}
                  selected={selectedMethod === MergeQueueMethod.GROUP}
                  onSelect={() => setSelectedMethod(MergeQueueMethod.GROUP)}
                />
                <MergeDropdownOption
                  primaryText="Queue and force solo merge"
                  secondaryText={`This pull request will be merged into ${baseRefName} by itself.`}
                  selected={selectedMethod === MergeQueueMethod.SOLO}
                  onSelect={() => setSelectedMethod(MergeQueueMethod.SOLO)}
                />
              </ActionList>
            }
            onPrimaryButtonClick={() => {
              if (confirming) {
                setErrorMessage(undefined)
                enableAutoMerge({mergeMethod: selectedMethod})
              } else {
                setConfirming(true)
              }
            }}
          >
            {isPending ? '' : primaryLabel}
          </ButtonWithDropdown>
        </div>
        <div className={`${styles.cancelButton} ${confirming ? styles.cancelButtonVisible : ''}`}>
          <IconButton
            icon={CircleSlashIcon}
            aria-label="Cancel"
            disabled={isPending}
            onClick={() => {
              setConfirming(false)
              setErrorMessage(undefined)
            }}
            tabIndex={confirming ? 0 : -1}
          />
        </div>
      </div>
      {mergeQueue && (
        <span className="text-small color-fg-muted">
          This repository uses the{' '}
          <Link href={mergeQueue.url} inline>
            merge queue
          </Link>{' '}
          for all merges into the {baseRefName} branch.
        </span>
      )}
    </div>
  )
}
